import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Columns3 } from 'lucide-react';

export type SiteColumnKey =
  | 'name'
  | 'account'
  | 'tags'
  | 'locations'
  | 'address'
  | 'status';

export const DEFAULT_VISIBLE_COLUMNS: SiteColumnKey[] = [
  'name',
  'account',
  'tags',
  'locations',
  'address',
  'status',
];

const COLUMN_LABELS: Record<SiteColumnKey, string> = {
  name: 'table.site',
  account: 'table.account',
  tags: 'table.tags',
  locations: 'table.locations',
  address: 'table.address',
  status: 'table.status',
};

interface ColumnVisibilityToggleProps {
  visibleColumns: SiteColumnKey[];
  onVisibleColumnsChange: (columns: SiteColumnKey[]) => void;
}

export function ColumnVisibilityToggle({
  visibleColumns,
  onVisibleColumnsChange,
}: ColumnVisibilityToggleProps) {
  const { t } = useTranslation('sites');

  const toggleColumn = (col: SiteColumnKey) => {
    if (visibleColumns.includes(col)) {
      if (col === 'name') return;
      onVisibleColumnsChange(visibleColumns.filter((c) => c !== col));
    } else {
      onVisibleColumnsChange([...visibleColumns, col]);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Columns3 className="h-4 w-4 mr-1" />
          {t('toolbar.columns', 'Columns')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {DEFAULT_VISIBLE_COLUMNS.map((col) => (
          <DropdownMenuCheckboxItem
            key={col}
            checked={visibleColumns.includes(col)}
            onCheckedChange={() => toggleColumn(col)}
            disabled={col === 'name'}
          >
            {t(COLUMN_LABELS[col], col)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
